import classnames from 'classnames';
import { useLang } from 'shared/hooks/lang.hooks';
import type { ContentTypeClassification } from 'shared/constants';
import type { ContentTypeChipView } from 'shared/panda/messages/doc-answer';
import styles from './answer-card.module.scss';

interface ContentTypeChipProps {
  chip: ContentTypeChipView;
  /** Hub answer: the accent fill replaces the authority-based one. */
  isHub?: boolean;
}

// `keyof typeof styles` is `string` while the `*.module.scss` ambient type is an index
// signature — it states which side of the record holds a class name, and tightens on its
// own if those modules ever get generated types. The key side is the real check.
const classificationToStyle: Record<ContentTypeClassification, keyof typeof styles> = {
  PRIMARY: 'chipPrimary',
  SECONDARY: 'chipSecondary',
};

/**
 * Pill naming the source's content type; background encodes its authority.
 * Interim implementation — tear down in favor of the design system's Tag
 * component (c8-design-system `components/tag`) once its in-app primitive lands.
 */
export function ContentTypeChip({ chip, isHub = false }: ContentTypeChipProps) {
  const langTags = useLang('TAGS');
  // Keyed lookup by a wire value that may name a type this build has no label for.
  const label = (langTags as Partial<Record<string, string>>)[chip.contentType];
  if (!label) return null;

  const fill = isHub ? styles.chipHub : styles[classificationToStyle[chip.classification]];

  return <span className={classnames(styles.contentTypeChip, fill)}>{label}</span>;
}
